import React, { Fragment, useEffect, useMemo, useState } from "react";

import { Button, Text } from "components";
import ContextMenu from "components/ContextMenu";
import Footer1 from "components/Footer1";
import InfoRibbon from "components/InfoRibbon";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { scrollToTop } from "../../helpers/scroll";
import { clearSearchKeyword } from "../../states/search.mjs";
import FormInput from "../../components/FormInput";
import { getOnlineOrdersApi } from "../../service/api";
import { toast } from "react-toastify";
import { startLoading, stopLoading } from "../../states/progress.mjs";
import { justArray } from "../../helpers/array";
import { formatCurrency } from "../../helpers/number";

function StatusLabel({ paid, processed, completed }) {
  return (
    <div className="flex flex-col justify-center items-center">
      {paid && <div className="bg-green-600 mb-2 p-1 rounded-lg px-3"><Text
        className="text-[14px] font-light text-white-A700">PAID</Text></div>}
      {!paid &&
        <div className="bg-red-300 mb-2 p-1 rounded-lg px-3"><Text className="text-[14px] font-light">NOT PAID</Text>
        </div>}
      {paid && processed &&
        <div className="bg-amber-500 mb-2 p-1 rounded-lg px-3"><Text className="text-[14px] font-light">PROCESSED</Text>
        </div>}
      {paid && !processed &&
        <div className="bg-red-300 mb-2 p-1 rounded-lg px-3"><Text className="text-[14px] font-light">NOT
          PROCESSED</Text></div>}
      {paid && processed && completed &&
        <div className="bg-blue-700 mb-2 p-1 rounded-lg px-3"><Text
          className="text-[14px] font-light text-white-A700">COMPLETED</Text></div>}
      {paid && processed && !completed &&
        <div className="bg-red-300 mb-2 p-1 rounded-lg px-3"><Text className="text-[14px] font-light">NOT
          COMPLETED</Text></div>}
    </div>
  );
}

function NoPaymentReference({ onChange = () => null, reference, onClick = () => null }) {
  const [error, setError] = useState();
  return (
    <div className="p-6 flex flex-col">
      {/*<Text className="text-2xl text-black-900_dd">Payment Reference</Text>*/}
      <Text className="text-sm mb-1 text-black-900_dd pb-4">
        Enter mobile number you use for payment to retrieve its orders
      </Text>
      <div className="pb-4">
        <FormInput errors={error ? [error] : []} label={""} onChange={(value) => {
          setError(undefined);
          onChange(value);
        }} className="w-full" />
      </div>
      <Button
        onClick={() => {
          if (!reference) {
            setError("Mobile number required");
          } else {
            onClick();
          }
        }}
        className="cursor-pointer flex items-center px-5 justify-center rounded-[18px] min-h-[48px]"
        shape="round"
        color="orange_A200"
        size="xs"
        variant="fill"
      >
        <div className="text-left text-base tracking-[0.25px]">
          GET ORDERS
        </div>
      </Button>
    </div>
  );
}

export const OrdersPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [orders, setOrders] = useState();
  const [paymentReference, setPaymentReference] = useState();

  const getOrders = useMemo(() => {
    return (reference) => {
      startLoading();
      getOnlineOrdersApi({ reference })
        .then((res) => {
          if (justArray(res?.data).length > 0) {
            setOrders(res?.data);
          } else {
            setOrders(undefined);
            toast.info("No orders for that reference, try to use different reference or try again.");
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error("Failed to retrieve orders, try to use different reference or try again.");
        }).finally(() => {
        stopLoading();
      });
    };
  }, []);

  useEffect(() => {
    if (state?._mobile) {
      setPaymentReference(state._mobile);
      getOrders(state._mobile);
    }
  }, [getOrders, state]);

  useEffect(() => {
    scrollToTop();
    return () => {
      clearSearchKeyword();
    };
  }, []);

  return (
    <>
      <div className="bg-white-A700 flex flex-col font-inter items-center justify-start mx-auto w-full">
        <InfoRibbon />
        <Header showSearch={false} />
        <ContextMenu />
        {
          !orders && <>
            <NoPaymentReference
              reference={paymentReference}
              onChange={setPaymentReference}
              onClick={() => getOrders(paymentReference)}
            />
          </>
        }
        {
          orders && <>
            <div className="p-6 w-full">
              <table className="table-auto border rounded-lg">
                <thead>
                <tr>
                  <th className="p-2">#ID</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Reference</th>
                  <th className="p-2">Items</th>
                  <th className="p-2">Amount ( TZS )</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Action</th>
                </tr>
                </thead>
                <tbody>
                {
                  justArray(orders).map((x, index) => {
                    return (
                      <Fragment key={x?.id + "a65487"}>
                        <tr className={index % 2 === 0 ? "bg-gray-100" : "bg-white-A700"}>
                          <td className="text-center p-2">{x?.id}</td>
                          <td className="text-center p-2">{x?.date}</td>
                          <td className="text-center p-2">{x?.payment_reference}</td>
                          <td className="text-center p-2">{justArray(x?.carts).length}</td>
                          <td className="text-center p-2">{formatCurrency(x?.amount)}</td>
                          <td className="text-center p-2">
                            <StatusLabel paid={x?.paid} completed={x?.completed} processed={x?.processed} />
                          </td>
                          <td className="text-center p-2">
                            <div className="p-6 flex justify-center items-center">
                              <Button
                                onClick={() => {
                                  navigate("/orderviewpage", { state: x });
                                }}
                                className="cursor-pointer flex items-center px-5 justify-center rounded-[18px]"
                                shape="round"
                                color="orange_A200"
                                size="xs"
                                variant="fill"
                              >
                                <div className="text-left text-sm tracking-[0.25px]">
                                  View
                                </div>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })
                }
                </tbody>
              </table>
            </div>
            <div className="w-full p-6">
              <Button
                onClick={() => {
                  setOrders(undefined);
                  setPaymentReference(undefined);
                }}
                className="cursor-pointer flex items-center px-5 justify-center rounded-[18px] min-h-[48px]"
                shape="round"
                color="indigo_A700"
                size="xs"
                variant="fill"
              >
                <div className="text-left text-base tracking-[0.25px]">
                  Change reference
                </div>
              </Button>
            </div>
          </>
        }
        <Footer1 className="bg-blue_gray-900 mt-16 flex flex-col items-center justify-start p-6 sm:px-5 w-full" />
      </div>
    </>
  );
};

export default OrdersPage;
